<template>
  <div>
    <div class="panel-header">
      <div>
        <div class="panel-title">岗位管理</div>
        <div class="panel-desc">维护岗位基础信息，并通过操作列配置岗位的角色、用户与数据权限</div>
      </div>
      <a-space>
        <a-input-search
          v-model="keyword"
          placeholder="按编码/名称过滤"
          style="width: 220px"
          allowClear
        />
        <a-button type="primary" icon="plus" @click="openAdd">新增岗位</a-button>
      </a-space>
    </div>

    <a-table
      bordered
      rowKey="id"
      size="middle"
      :loading="loading"
      :columns="columns"
      :dataSource="filteredPositions"
      :pagination="false"
    >
      <template slot="levelCol" slot-scope="text">
        <a-tag :color="text === 0 ? 'purple' : 'blue'">{{ text === 0 ? "公司级" : "部门级" }}</a-tag>
      </template>
      <template slot="statusCol" slot-scope="text">
        <a-badge :status="text === 'active' ? 'success' : 'error'" :text="text === 'active' ? '启用' : '停用'" />
      </template>
      <template slot="keyCol" slot-scope="text">
        <a-tag v-if="text" color="red">关键岗位</a-tag>
      </template>
      <template slot="actionCol" slot-scope="text, record">
        <a-space>
          <a @click="openEdit(record)">编辑</a>
          <a-divider type="vertical" />
          <a @click="openUsers(record)">用户</a>
          <a-divider type="vertical" />
          <a @click="openRoles(record)">角色</a>
          <a-divider type="vertical" />
          <a @click="openDataPerms(record)">数据权限</a>
          <a-divider type="vertical" />
          <a class="danger-link" @click="deleteText(record)">删除</a>
        </a-space>
      </template>
    </a-table>

    <!-- 新增/编辑岗位 -->
    <a-modal
      :title="editingId ? '编辑岗位' : '新增岗位'"
      :visible="modalVisible"
      :confirm-loading="modalLoading"
      @ok="submitModal"
      @cancel="modalVisible = false"
    >
      <a-form-model
        ref="positionForm"
        :model="modalForm"
        :rules="modalRules"
        :label-col="{ span: 5 }"
        :wrapper-col="{ span: 17 }"
      >
        <a-form-model-item label="岗位编码" prop="code">
          <a-input v-model="modalForm.code" placeholder="如：POS_MANAGER" />
        </a-form-model-item>
        <a-form-model-item label="岗位名称" prop="name">
          <a-input v-model="modalForm.name" placeholder="如：部门负责人" />
        </a-form-model-item>
        <a-form-model-item label="岗位级别" prop="level">
          <a-select v-model="modalForm.level">
            <a-select-option :value="0">公司级</a-select-option>
            <a-select-option :value="1">部门级</a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="所属部门">
          <a-select
            v-model="modalForm.deptId"
            show-search
            allow-clear
            option-filter-prop="children"
            placeholder="请选择部门（部门级岗位建议选择）"
          >
            <a-select-option v-for="item in orgList" :key="item.id" :value="item.id">
              {{ item.uniqueName || item.name }}
            </a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="关键岗位">
          <a-checkbox :checked="modalForm.isKeyPosition" @change="(e) => (modalForm.isKeyPosition = e.target.checked)">
            标记为关键岗位
          </a-checkbox>
        </a-form-model-item>
        <a-form-model-item label="状态">
          <a-radio-group v-model="modalForm.status">
            <a-radio value="active">启用</a-radio>
            <a-radio value="inactive">停用</a-radio>
          </a-radio-group>
        </a-form-model-item>
        <a-form-model-item label="备注">
          <a-textarea v-model="modalForm.remarks" :rows="2" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <!-- 岗位-用户 -->
    <position-user-modal :visible.sync="userModalVisible" :position="currentPosition" />
    <!-- 岗位-角色 -->
    <position-role-modal :visible.sync="roleModalVisible" :position="currentPosition" />
    <!-- 岗位-数据权限 -->
    <position-dp-modal :visible.sync="dpModalVisible" :position="currentPosition" />
  </div>
</template>

<script>
import {
  fetchPositionList,
  addPosition,
  updatePosition,
  deletePosition,
} from "@/api/setting/auth";
import { fetchList as fetchOrgList } from "@/api/setting/organization";
import PositionUserModal from "./position-user-modal";
import PositionRoleModal from "./position-role-modal";
import PositionDpModal from "./position-dp-modal";

export default {
  name: "position-manage",

  components: { PositionUserModal, PositionRoleModal, PositionDpModal },

  data() {
    return {
      keyword: "",
      loading: false,
      positions: [],
      orgList: [],
      columns: [
        { title: "岗位编码", dataIndex: "code", width: 140 },
        { title: "岗位名称", dataIndex: "name", width: 160 },
        { title: "级别", dataIndex: "level", width: 90, align: "center", scopedSlots: { customRender: "levelCol" } },
        { title: "所属部门", dataIndex: "deptName", width: 150 },
        { title: "关键岗位", dataIndex: "isKeyPosition", width: 90, align: "center", scopedSlots: { customRender: "keyCol" } },
        { title: "状态", dataIndex: "status", width: 80, align: "center", scopedSlots: { customRender: "statusCol" } },
        { title: "备注", dataIndex: "remarks", ellipsis: true },
        { title: "操作", width: 280, align: "center", scopedSlots: { customRender: "actionCol" } },
      ],
      modalVisible: false,
      modalLoading: false,
      editingId: null,
      modalForm: {},
      modalRules: {
        code: [{ required: true, message: "请输入岗位编码" }],
        name: [{ required: true, message: "请输入岗位名称" }],
        level: [{ required: true, message: "请选择岗位级别" }],
      },
      userModalVisible: false,
      roleModalVisible: false,
      dpModalVisible: false,
      currentPosition: {},
    };
  },

  computed: {
    filteredPositions() {
      const kw = (this.keyword || "").trim().toLowerCase();
      if (!kw) {
        return this.positions;
      }
      return this.positions.filter(
        (p) =>
          (p.code || "").toLowerCase().includes(kw) ||
          (p.name || "").toLowerCase().includes(kw)
      );
    },
  },

  mounted() {
    this.getList();
    fetchOrgList({ status: 1 })
      .then((res) => {
        this.orgList = Array.isArray(res) ? res : res.list || [];
      })
      .catch(() => {});
  },

  methods: {
    getList() {
      this.loading = true;
      fetchPositionList()
        .then((res) => {
          this.loading = false;
          this.positions = Array.isArray(res) ? res : [];
        })
        .catch(() => {
          this.loading = false;
        });
    },
    openAdd() {
      this.editingId = null;
      // 预置全部字段，保证 Vue2 响应式
      this.modalForm = {
        code: undefined,
        name: undefined,
        level: 1,
        deptId: undefined,
        isKeyPosition: false,
        status: "active",
        remarks: undefined,
      };
      this.modalVisible = true;
    },
    openEdit(record) {
      this.editingId = record.id;
      this.modalForm = {
        code: record.code,
        name: record.name,
        level: record.level,
        deptId: record.deptId,
        isKeyPosition: !!record.isKeyPosition,
        status: record.status,
        remarks: record.remarks,
      };
      this.modalVisible = true;
    },
    submitModal() {
      this.$refs.positionForm.validate((valid) => {
        if (!valid) {
          return;
        }
        // 部门名称随所选部门带入
        const dept = this.orgList.find((item) => item.id === this.modalForm.deptId);
        const payload = {
          ...this.modalForm,
          deptName: dept ? dept.uniqueName || dept.name : null,
        };
        this.modalLoading = true;
        const req = this.editingId
          ? updatePosition({ ...payload, id: this.editingId })
          : addPosition(payload);
        req
          .then(() => {
            this.modalLoading = false;
            this.modalVisible = false;
            this.$message.success(this.editingId ? "修改成功" : "新增成功");
            this.getList();
          })
          .catch(() => {
            this.modalLoading = false;
          });
      });
    },
    deleteText(record) {
      const that = this;
      this.$confirm({
        title: `确认删除岗位「${record.name}」吗？`,
        content: "删除后该岗位将不可用，请先确认无用户持有该岗位。",
        onOk() {
          return deletePosition(record.id).then(() => {
            that.$message.success("删除成功");
            that.getList();
          });
        },
      });
    },
    openUsers(record) {
      this.currentPosition = record;
      this.userModalVisible = true;
    },
    openRoles(record) {
      this.currentPosition = record;
      this.roleModalVisible = true;
    },
    openDataPerms(record) {
      this.currentPosition = record;
      this.dpModalVisible = true;
    },
  },
};
</script>

<style scoped>
.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}
.panel-title {
  font-size: 16px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}
.panel-desc {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}
.danger-link {
  color: #f5222d;
}
</style>
